import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useInvitations } from '../context/InvitationContext';
import { User, LogOut, ChevronDown, Mail, Building2 } from 'lucide-react';
import { API_BASE, BACKEND_URL } from '../config/api';

const API = API_BASE;

const UserMenu = () => {
  const { user, logout } = useAuth();
  const { invitationCount } = useInvitations();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handleProfile = () => {
    setIsOpen(false);
    navigate('/profile');
  };

  const handleInvitations = () => {
    setIsOpen(false);
    navigate('/invitations');
  };

  const handleWorkspaces = () => {
    setIsOpen(false);
    navigate('/workspaces');
  };

  const handleBilling = () => {
    setIsOpen(false);
    navigate('/billing');
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-secondary transition-colors relative"
        data-testid="user-menu-button"
      >
        {user.avatar_url ? (
          <img
            src={`${BACKEND_URL}${user.avatar_url}`}
            alt={user.name}
            className="h-8 w-8 rounded-full object-cover border border-border"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
            <User className="h-4 w-4 text-primary-foreground" />
          </div>
        )}
        <span className="text-sm font-medium hidden md:block">{user.name}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {invitationCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
            {invitationCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-sm shadow-lg z-50">
          <div className="p-3 border-b border-border">
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <div className="py-1">
            <button
              onClick={handleProfile}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2"
              data-testid="profile-menu-item"
            >
              <User className="h-4 w-4" />
              Profile
            </button>
            <button
              onClick={handleWorkspaces}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2"
              data-testid="workspaces-menu-item"
            >
              <Building2 className="h-4 w-4" />
              Workspaces
            </button>
            <button
              onClick={handleInvitations}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2 relative"
              data-testid="invitations-menu-item"
            >
              <Mail className="h-4 w-4" />
              Invitations
              {invitationCount > 0 && (
                <span className="ml-auto h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  {invitationCount}
                </span>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 text-sm hover:bg-destructive/10 text-destructive transition-colors flex items-center gap-2"
              data-testid="logout-menu-item"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
